import React, { useState, useCallback, useEffect } from "react"
import DateUtil from "../../utils/Date"
import API from "../../utils/API"
import TaskManagerModal from "./TaskManagerModal"
import DeviceLogsModal from "./DeviceLogsModal"
import AiDebugModal from "./AiDebugModal"
import RemoteDeviceManager from "./RemoteDeviceManager"

const CONNECTION_STATUS_BADGES = {
  connected: { className: "badge-success", label: "Connecté" },
  disconnected: { className: "badge-warning", label: "Déconnecté" },
  error: { className: "badge-danger", label: "Erreur" },
  unknown: { className: "badge-secondary", label: "Inconnu" },
}

const ACTIVATION_STATUS_BADGES = {
  activated: { className: "badge-success", label: "Activé" },
  pending_activation: { className: "badge-info", label: "En cours" },
  discovered: { className: "badge-warning", label: "Découvert" },
  not_found: { className: "badge-secondary", label: "Non trouvé" },
  error: { className: "badge-danger", label: "Erreur" },
}

function DeviceInfoModal({ totemId, onClose }) {
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchDeviceInfo = useCallback(() => {
    setLoading(true)
    setError(null)
    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/device-info`,
      {},
      (response) => {
        setDeviceInfo(response.data)
        setLoading(false)
      },
      (errorMessage) => {
        setError(errorMessage)
        setLoading(false)
      },
    )
  }, [totemId])

  useEffect(() => {
    fetchDeviceInfo()
  }, [fetchDeviceInfo])

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-info-circle mr-2"></i>
                Détails du module IA
              </h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">
              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <p className="mt-2 text-muted">Interrogation du module IA en cours...</p>
                </div>
              )}

              {error && !loading && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  {error}
                </div>
              )}

              {deviceInfo && !loading && (
                <>
                  {deviceInfo.device && (
                    <>
                      <h6 className="text-muted text-uppercase mb-2">
                        <i className="fas fa-microchip mr-1"></i> Identité du périphérique
                      </h6>
                      <table className="table table-sm table-bordered mb-3">
                        <tbody>
                          <tr>
                            <th style={{ width: "35%" }}>Nom</th>
                            <td>{deviceInfo.device.deviceName || "-"}</td>
                          </tr>
                          <tr>
                            <th>Fabricant</th>
                            <td>{deviceInfo.device.manufacturer || "-"}</td>
                          </tr>
                          <tr>
                            <th>Modèle</th>
                            <td>{deviceInfo.device.deviceModel || "-"}</td>
                          </tr>
                          <tr>
                            <th>N° de série</th>
                            <td>{deviceInfo.device.deviceSn || "-"}</td>
                          </tr>
                          <tr>
                            <th>Version matérielle</th>
                            <td>{deviceInfo.device.hardwareVersion || "-"}</td>
                          </tr>
                          <tr>
                            <th>Firmware</th>
                            <td>{deviceInfo.device.firmwareVersion || "-"}</td>
                          </tr>
                          <tr>
                            <th>Version algorithme</th>
                            <td>{deviceInfo.device.algorithmVersion || "-"}</td>
                          </tr>
                          <tr>
                            <th>Puce</th>
                            <td>{deviceInfo.device.chip || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  )}

                  {deviceInfo.runtime && (
                    <>
                      <h6 className="text-muted text-uppercase mb-2">
                        <i className="fas fa-heartbeat mr-1"></i> État du système
                      </h6>
                      <table className="table table-sm table-bordered mb-3">
                        <tbody>
                          <tr>
                            <th style={{ width: "35%" }}>CPU</th>
                            <td>
                              {deviceInfo.runtime.cpuUsage != null ? `${deviceInfo.runtime.cpuUsage}%` : "-"}
                            </td>
                          </tr>
                          <tr>
                            <th>Température CPU</th>
                            <td>
                              {deviceInfo.runtime.cpuTemperature != null
                                ? `${deviceInfo.runtime.cpuTemperature}°C`
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <th>Température GPU</th>
                            <td>
                              {deviceInfo.runtime.gpuTemperature != null
                                ? `${deviceInfo.runtime.gpuTemperature}°C`
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <th>Température carte</th>
                            <td>
                              {deviceInfo.runtime.boardTemperature != null
                                ? `${deviceInfo.runtime.boardTemperature}°C`
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <th>Mémoire</th>
                            <td>
                              {deviceInfo.runtime.memoryUsed != null && deviceInfo.runtime.memoryTotal != null
                                ? `${deviceInfo.runtime.memoryUsed} / ${deviceInfo.runtime.memoryTotal} Mo`
                                : "-"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  )}

                  {deviceInfo.intelligence && (
                    <>
                      <h6 className="text-muted text-uppercase mb-2">
                        <i className="fas fa-brain mr-1"></i> Intelligence
                      </h6>
                      <div className="mb-2">
                        <strong>Algorithmes supportés :</strong>{" "}
                        {deviceInfo.intelligence.supportedAlgorithms?.length > 0 ? (
                          deviceInfo.intelligence.supportedAlgorithms.map((algo) => (
                            <span key={algo} className="badge badge-info mr-1">
                              {algo}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted">Aucun</span>
                        )}
                      </div>
                      {deviceInfo.intelligence.activeMonitors?.length > 0 && (
                        <div className="mb-2">
                          <strong>Moniteurs actifs :</strong>{" "}
                          {deviceInfo.intelligence.activeMonitors.map((m, i) => (
                            <span key={i} className="badge badge-success mr-1">
                              {typeof m === 'string' ? m : (m.monitorId || m.name || '-')}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-info"
                onClick={fetchDeviceInfo}
                disabled={loading}
              >
                <i className="fas fa-sync-alt mr-1"></i> Rafraîchir
              </button>
              <button type="button" className="btn btn-default" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}

export default function AiTargetingModuleStatusCard({ status, totemId }) {
  const [showPassword, setShowPassword] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [taskManagerModalOpen, setTaskManagerModalOpen] = useState(false)
  const [logsModalOpen, setLogsModalOpen] = useState(false)
  const [debugModalOpen, setDebugModalOpen] = useState(false)

  if (!status) return null

  const connBadge = CONNECTION_STATUS_BADGES[status.connectionStatus] || CONNECTION_STATUS_BADGES.unknown
  const actBadge = ACTIVATION_STATUS_BADGES[status.activationStatus] || ACTIVATION_STATUS_BADGES.not_found

  return (
    <div className="p-3">
      <div className="row mb-3">
        <div className="col-sm-6">
          <strong>Connexion :</strong>{" "}
          <span className={`badge ${connBadge.className}`}>{connBadge.label}</span>
        </div>
        <div className="col-sm-6">
          <strong>Activation :</strong>{" "}
          <span className={`badge ${actBadge.className}`}>{actBadge.label}</span>
        </div>
      </div>

      <table className="table table-sm table-bordered mb-3" style={{ tableLayout: "fixed" }}>
        <tbody>
          <tr>
            <th style={{ width: "35%" }}>Adresse IP</th>
            <td>{status.ipAddress || "-"}</td>
          </tr>
          <tr>
            <th>Identifiant</th>
            <td>{status.username || "-"}</td>
          </tr>
          <tr>
            <th>Mot de passe</th>
            <td>
              <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                <span className="mr-2" style={{ wordBreak: "break-all" }}>
                  {status.password ? (showPassword ? status.password : "••••••••") : "-"}
                </span>
                {status.password && (
                  <button
                    type="button"
                    className="btn btn-xs btn-default"
                    onClick={() => setShowPassword((prev) => !prev)}
                    title={showPassword ? "Masquer" : "Afficher"}
                  >
                    <i className={`fas fa-eye${showPassword ? "-slash" : ""}`}></i>
                  </button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="row text-muted mb-3" style={{ fontSize: "0.85rem" }}>
        <div className="col-sm-6">
          <i className="fas fa-heartbeat mr-1"></i>
          Dernier check : {status.lastHealthCheckAt ? DateUtil.formatDate(status.lastHealthCheckAt) : "-"}
        </div>
        {status.lastConnectionError && (
          <div className="col-sm-6 text-danger">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            {status.lastConnectionError}
          </div>
        )}
      </div>

      <button
        className="btn btn-outline-info btn-sm"
        onClick={() => setModalOpen(true)}
        disabled={status.connectionStatus !== "connected"}
        title={status.connectionStatus !== "connected" ? "Le module doit être connecté" : ""}
      >
        <i className="fas fa-info-circle mr-1"></i> Détails du module
      </button>

      <button
        className="btn btn-outline-success btn-sm ml-2"
        onClick={() => setTaskManagerModalOpen(true)}
        disabled={status.connectionStatus !== "connected"}
        title={status.connectionStatus !== "connected" ? "Le module doit être connecté" : ""}
      >
        <i className="fas fa-tasks mr-1"></i> Gestion Tâches IA
      </button>

      <button
        className="btn btn-outline-warning btn-sm ml-2"
        onClick={() => setLogsModalOpen(true)}
        disabled={status.connectionStatus !== "connected"}
        title={status.connectionStatus !== "connected" ? "Le module doit être connecté" : ""}
      >
        <i className="fas fa-file-alt mr-1"></i> Logs du module
      </button>

      <button
        className="btn btn-outline-secondary btn-sm ml-2"
        onClick={() => setDebugModalOpen(true)}
        disabled={status.connectionStatus !== "connected"}
        title={status.connectionStatus !== "connected" ? "Le module doit être connecté" : ""}
      >
        <i className="fas fa-bug mr-1"></i> Debug
      </button>

      {modalOpen && <DeviceInfoModal totemId={totemId} onClose={() => setModalOpen(false)} />}

      {taskManagerModalOpen && (
        <TaskManagerModal totemId={totemId} onClose={() => setTaskManagerModalOpen(false)} />
      )}

      {logsModalOpen && (
        <DeviceLogsModal totemId={totemId} onClose={() => setLogsModalOpen(false)} />
      )}

      {debugModalOpen && (
        <AiDebugModal totemId={totemId} onClose={() => setDebugModalOpen(false)} />
      )}

      <RemoteDeviceManager totemId={totemId} disabled={status.connectionStatus !== "connected"} />
    </div>
  )
}
